import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SUPPORTED_DATE_FORMATS, SUPPORTED_LANGUAGES, SUPPORTED_METRIC_UNITS, SUPPORTED_TIMEZONES } from '@app-core/constants/constants';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { Observable, of, Subject } from 'rxjs';
import { map, shareReplay, startWith, takeUntil } from 'rxjs/operators';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
})
export class UserSettingsComponent implements OnInit {
  public supportedTimeZones = SUPPORTED_TIMEZONES;
  public supportedMetricUnits = SUPPORTED_METRIC_UNITS;
  public supportedDateFormats = SUPPORTED_DATE_FORMATS;
  public supportedLanguages = SUPPORTED_LANGUAGES;
  public userSettingsForm: FormGroup;
  public isDirty$: Observable<boolean> = of(false);
  public selectedTimezone: string = '';
  private ngUnsubscribe = new Subject<void>();
  filteredTimezones: Observable<any[]>;
  public customOptions = {
    showLabel: true,
  };
  constructor(
    public dialogRef: MatDialogRef<UserSettingsComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    private fb: FormBuilder,
    private gtmService: GoogleTagManagerService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit(): void {
    this.createForm();
  }

  private createForm() {
    const { currentTimeZone = '', currentMetricUnit = '', currentDateFormat = '', currentLanguage = '' } = this.data || {};
    this.selectedTimezone = currentTimeZone;
    this.userSettingsForm = this.fb.group({
      timezone: this.fb.control(currentTimeZone, Validators.required),
      metricUnit: this.fb.control(currentMetricUnit, Validators.required),
      dateFormat: this.fb.control(currentDateFormat, Validators.required),
      language: this.fb.control(currentLanguage, Validators.required),
    });

    this.formValueChanges();
    this.filteredTimezones = this.userSettingsForm.get('timezone').valueChanges.pipe(
      startWith(''),
      map((value) => this._filterTimezones(value))
    );
  }

  private formValueChanges() {
    if (this.userSettingsForm) {
      this.isDirty$ = this.userSettingsForm.valueChanges.pipe(
        takeUntil(this.ngUnsubscribe),
        shareReplay(1),
        map(() => this.userSettingsForm.dirty)
      );
      this.isDirty$.subscribe();
    }
  }

  public onSubmit() {
    if (this.userSettingsForm.invalid) {
      return;
    }
    const oldTimeZone = this.data.currentTimeZone;
    const oldMetricUnit = this.data.currentMetricUnit;
    const oldDateFormat = this.data.currentDateFormat;
    const oldLanguage = this.data.currentLanguage;

    const { timezone, metricUnit, dateFormat, language } = this.userSettingsForm.getRawValue();

    const newTimeZone = timezone;
    const newMetricUnit = metricUnit;
    const newDateFormat = dateFormat;
    const newLanguage = language;

    if (oldDateFormat !== newDateFormat) {
      this.gtmService.userPrefrenceChangeDateFormat(newDateFormat);
      this.mixpanelService.track('Change Date Format', {
        'Date Format': newDateFormat,
      });
    }
    if (oldLanguage !== newLanguage) {
      this.gtmService.userPrefrenceChangeLanguageChange(newLanguage);
      this.mixpanelService.track('Change Language', {
        Language: newLanguage,
      });
    }
    if (oldMetricUnit !== newMetricUnit) {
      this.gtmService.userPrefrenceChangeMetricUnitChange(newMetricUnit);
      this.mixpanelService.track('Change Metric Unit', {
        'Metric Unit': newMetricUnit,
      });
    }
    if (oldTimeZone !== newTimeZone) {
      this.gtmService.userPrefrenceChangeTimeZoneChange(newTimeZone);
      this.mixpanelService.track('Change TimeZone', {
        'Time Zone': newTimeZone,
      });
    }

    this.onClose({
      timezone,
      metricUnit,
      dateFormat,
      language,
    });
  }

  private _filterTimezones(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.supportedTimeZones.filter((timezone) => timezone.key.toLowerCase().includes(filterValue));
  }

  clearTimezone() {
    this.userSettingsForm.get('timezone').setValue('');
  }

  public onClose(data: any) {
    this.dialogRef.close(data);
  }
  public onTimezoneSelect(selectedTimezone = '') {
    this.userSettingsForm.get('timezone').setValue(selectedTimezone);
    if (this.data) {
      this.data.currentTimeZone = selectedTimezone;
    }
  }

  public timezoneDisplayFn(timezoneValue: string): string {
    const timezoneOption = SUPPORTED_TIMEZONES.find((t) => t.value === timezoneValue);
    return timezoneOption?.customLabel || timezoneValue;
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
